import { Injectable } from "@angular/core";
import { Observable, throwError } from "rxjs";
import { tap, catchError } from "rxjs/operators";
import {
  QPilotService,
  QPilotAccessToken,
} from "../../../core/services/qpilot.service";
import { SessionService } from "../../../core/services/session.service";
import { MessageService } from "primeng/api";

/**
 * Service for managing scheduled order processing operations
 * Handles "Process Now" and queue management for scheduled orders
 */
@Injectable({ providedIn: "root" })
export class ScheduledOrderProcessingService {
  constructor(
    private qpilotService: QPilotService,
    private sessionService: SessionService,
    private messageService: MessageService,
  ) {}

  /**
   * Process a scheduled order immediately (queue for immediate processing)
   * @param siteId Site ID
   * @param scheduledOrderId Scheduled order ID
   * @param accessToken Optional access token for embedded mode
   * @returns Observable indicating success
   */
  processNow(
    siteId: number,
    scheduledOrderId: number,
    accessToken?: QPilotAccessToken,
  ): Observable<void> {
    const token = accessToken ?? this.sessionService.accessToken;
    if (!token) {
      return throwError(() => new Error("No access token available"));
    }
    return this.qpilotService
      .processScheduledOrderNow(siteId, scheduledOrderId, token, true)
      .pipe(
        tap(() => {
          this.messageService.add({
            severity: "success",
            summary: "Success",
            detail: "Scheduled order queued for processing",
            life: 3000,
          });
        }),
        catchError((error) =>
          this.handleError(error, "process scheduled order"),
        ),
      );
  }

  /**
   * Check if a scheduled order is currently being processed
   * @param siteId Site ID
   * @param scheduledOrderId Scheduled order ID
   * @returns Observable<boolean> indicating if order is processing
   */
  isProcessing(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    siteId: number,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    scheduledOrderId: number,
  ): Observable<boolean> {
    // Implementation depends on API structure
    // May check order Status field or LastProcessingCycle
    // For now, return false until we have the data structure confirmed
    return throwError(
      () => new Error("isProcessing check not yet implemented"),
    );
  }

  /**
   * Handle API errors with user-friendly messages
   * @param error Error object
   * @param operation Operation description
   * @returns Observable error
   */
  private handleError(error: unknown, operation: string): Observable<never> {
    let errorMessage = `Failed to ${operation}`;

    if (error && typeof error === "object") {
      const errorObj = error as {
        error?: { message?: string; Message?: string };
        message?: string;
        Message?: string;
      };

      // Try different possible error message paths
      if (errorObj.error?.message) {
        errorMessage = errorObj.error.message;
      } else if (errorObj.error?.Message) {
        errorMessage = errorObj.error.Message;
      } else if (errorObj.message) {
        errorMessage = errorObj.message;
      } else if (errorObj.Message) {
        errorMessage = errorObj.Message;
      }
    }

    this.messageService.add({
      severity: "error",
      summary: "Error",
      detail: errorMessage,
      life: 5000,
    });

    return throwError(() => new Error(errorMessage));
  }
}
